import React from 'react';
import { RotateCcw, ArrowUp, KeyRound, Clock, FolderOpen, Headphones, LogOut } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { PixelTrail } from '@/components/ui/pixel-trail';
import { useScreenSize } from '@/components/hooks/use-screen-size';
import { useNavigate } from 'react-router-dom';
import shomriLogo from '@/assets/shomri-logo.svg';
import { InteractiveStepAccordion, StepItem } from '@/components/ui/interactive-step-accordion';

const ConfirmationPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const screenSize = useScreenSize();

  const steps: StepItem[] = [
    {
      id: 1,
      icon: <RotateCcw className="w-6 h-6" />,
      title: "Device Restart",
      description: "Your iPhone will restart now. This is normal and required to complete the installation.",
      accentColor: "bg-shomri-purple"
    },
    {
      id: 2,
      icon: <ArrowUp className="w-6 h-6" />,
      title: "Swipe Up",
      description: "After restart, swipe up on the screen to begin the upgrade process.",
      accentColor: "bg-shomri-blue"
    },
    {
      id: 3,
      icon: <KeyRound className="w-6 h-6" />,
      title: "Enroll",
      description: "You'll see a prompt to enroll with Shomri. Enter your code when asked.",
      accentColor: "bg-shomri-pink"
    },
    {
      id: 4,
      icon: <Clock className="w-6 h-6" />,
      title: "Wait 5 Min",
      description: "Settings take up to 5 minutes to apply. Shomri apps will download during this time.",
      accentColor: "bg-shomri-yellow"
    },
    {
      id: 5,
      icon: <FolderOpen className="w-6 h-6" />,
      title: "Apps Move",
      description: "Apps may not be in the same folders. They might appear individually—this is normal.",
      accentColor: "bg-shomri-green"
    },
    {
      id: 6,
      icon: <Headphones className="w-6 h-6" />,
      title: "Need Help?",
      description: "Open the Shomri app and tap Support to report any issues or get assistance.",
      accentColor: "bg-shomri-navy"
    }
  ];

  return (
    <div className="relative min-h-full flex items-center justify-center overflow-hidden py-8">
      {/* Interactive pixel trail background with Shomri green */}
      <div className="absolute inset-0 z-0">
        <PixelTrail
          pixelSize={screenSize.lessThan('md') ? 48 : 64}
          fadeDuration={0}
          delay={800}
          pixelClassName="rounded-full bg-shomri-green/40"
        />
      </div>

      {/* Fun gradient overlays with Shomri brand colors */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-shomri-purple/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-shomri-blue/15 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-shomri-pink/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-56 h-56 bg-shomri-green/10 rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 pointer-events-none">
        <Card className="border-0 bg-card/95 backdrop-blur-xl shadow-2xl pointer-events-auto overflow-visible">
          <CardContent className="pt-8 pb-6 px-6">
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-shomri-navy mb-2">
                What's Next?
              </h1>
              <p className="text-muted-foreground">
                Hover over each step to see what happens next
              </p>
            </div>

            {/* Interactive Step Accordion */}
            <InteractiveStepAccordion 
              steps={steps} 
              defaultActiveIndex={0}
              className="mb-6"
            />

            {/* Setup another device button */}
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-shomri-navy/20 text-shomri-navy hover:bg-shomri-navy/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Setup Another Device
            </Button>

            {/* Shomri Logo at bottom */}
            <div className="mt-6 pt-4 border-t border-border/50">
              <img src={shomriLogo} alt="Shomri" className="h-6 mx-auto opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConfirmationPage;
